/**
 * Moves the UI through the states of UX-SPEC §4.1: intake, fetch, worker messages, cancel, crash detection, restore.
 * The main thread does no parsing and no analysis (UX-FLOW-03): it reads file bytes, fetches URLs and hands both to the
 * worker.
 */
import type { AnalyseSource, ExportFormat, FromWorker, RecentItem } from '../shared/protocol.ts';
import { fmtBytes, fmtInt } from './format.ts';
import { recordAnalysisRate } from './rates.ts';
import { navigate } from './router.ts';
import { lightSniff } from './sniff.ts';
import {
	type AppError,
	addNotice,
	clearNotices,
	error,
	fetchHost,
	file,
	intakeInfo,
	markBootRestored,
	pasteIsSample,
	pasteText,
	pasteWords,
	phase,
	progress,
	recent,
	result,
	runStartedAt,
	setPhase,
	sourceName,
	storedJob,
	tab,
	urlText,
	wallMs,
} from './state.ts';
import { ERRORS, STATE_TEXT as T } from './strings.ts';
import { client } from './worker-client.ts';

export const MAX_FILE_BYTES = 100 * 1024 * 1024;
const SILENCE_MS = 15_000;

let currentJob = 0;
let abort: AbortController | null = null;
let lastMessageAt = 0;
let watchdog: number | null = null;
let restoredNotice = false;
/** The job id of the boot's own restore: its result is the same session continuing (UD-28). */
let bootRestoreId = -1;

function touch(): void {
	lastMessageAt = performance.now();
}

function startWatchdog(): void {
	touch();
	if (watchdog !== null) return;
	watchdog = window.setInterval(() => {
		const p = phase.value;
		if ((p === 'parsing' || p === 'analysing') && performance.now() - lastMessageAt > SILENCE_MS)
			crashed();
	}, 1000);
}

function stopWatchdog(): void {
	if (watchdog !== null) window.clearInterval(watchdog);
	watchdog = null;
}

function crashed(): void {
	stopWatchdog();
	client.restart();
	const p = phase.value;
	if (p === 'ready' || p === 'empty') return; // a crash with nothing running is not a state the spec names
	error.value = { code: 'crashed', message: ERRORS.crashed, action: 'run-again', at: Date.now() };
	setPhase('crashed');
}

client.onCrash = crashed;

/* ---------------- intake ---------------- */

export async function chooseFile(f: File): Promise<void> {
	if (f.size > MAX_FILE_BYTES) {
		error.value = {
			code: 'too-big',
			message: ERRORS.tooBig(fmtBytes(f.size)),
			action: 'choose-file',
			at: Date.now(),
		};
		setPhase('error');
		return;
	}
	setPhase('reading');
	runStartedAt.value = performance.now(); // the panel's elapsed time counts from here
	sourceName.value = f.name;
	error.value = null;
	try {
		const bytes = new Uint8Array(await f.arrayBuffer());
		file.value = {
			name: f.name,
			size: f.size,
			bytes,
			format: lightSniff(bytes.subarray(0, 4096), f.name),
		};
		tab.value = 'document';
		setPhase('empty');
	} catch {
		error.value = { code: 'read', message: ERRORS.fileRead, action: 'choose-file', at: Date.now() };
		setPhase('error');
	}
}

let countTimer: number | null = null;

/** Live word count of the paste box, computed in the worker and debounced 300 ms (§4.2). */
export function pasteChanged(text: string): void {
	pasteText.value = text;
	pasteIsSample.value = false;
	if (countTimer !== null) window.clearTimeout(countTimer);
	if (text.trim() === '') {
		pasteWords.value = null;
		return;
	}
	countTimer = window.setTimeout(() => {
		const id = client.id();
		client
			.request({ type: 'count', id, text }, ['counted'])
			.then((r) => {
				if (pasteText.value === text) pasteWords.value = r.words;
			})
			.catch(() => undefined);
	}, 300);
}

export function loadSample(sample: string): void {
	pasteText.value = sample;
	pasteIsSample.value = true;
	tab.value = 'paste';
	const id = client.id();
	client
		.request({ type: 'count', id, text: sample }, ['counted'])
		.then((r) => {
			pasteWords.value = r.words;
		})
		.catch(() => undefined);
}

export function hasInput(): boolean {
	switch (tab.value) {
		case 'document':
			return file.value !== null;
		case 'paste':
			return pasteText.value.trim() !== '';
		case 'url':
			return urlText.value.trim() !== '';
	}
}

/* ---------------- running ---------------- */

function begin(name: string): number {
	clearNotices();
	error.value = null;
	intakeInfo.value = null;
	result.value = null;
	wallMs.value = null;
	sourceName.value = name;
	progress.value = { stage: 'read', pct: 0 };
	runStartedAt.value = performance.now();
	currentJob = client.id();
	return currentJob;
}

function sendAnalyse(id: number, source: AnalyseSource): void {
	const transfer: Transferable[] = [];
	if (source.kind !== 'text') transfer.push(source.bytes.buffer as ArrayBuffer);
	startWatchdog();
	setPhase('parsing');
	client.send({ type: 'analyse', id, source }, transfer);
}

export async function run(): Promise<void> {
	if (!hasInput()) {
		error.value = {
			code: 'empty-input',
			message: ERRORS.emptyInput,
			action: 'choose-file',
			at: Date.now(),
		};
		setPhase('error');
		return;
	}
	switch (tab.value) {
		case 'document': {
			const f = file.value;
			if (!f) return;
			const id = begin(f.name);
			// the worker gets a copy: the input stays here so that Run again and Try again need no second read
			sendAnalyse(id, { kind: 'bytes', bytes: f.bytes.slice(), name: f.name });
			return;
		}
		case 'paste': {
			const id = begin(pasteIsSample.value ? 'Sample text' : 'Pasted text');
			sendAnalyse(id, { kind: 'text', text: pasteText.value, sample: pasteIsSample.value });
			return;
		}
		case 'url':
			return runUrl();
	}
}

function validUrl(text: string): URL | null {
	try {
		const u = new URL(text.trim());
		return u.protocol === 'http:' || u.protocol === 'https:' ? u : null;
	} catch {
		return null;
	}
}

function fetchError(code: string, retryAfter?: number): AppError {
	const f = ERRORS.fetch;
	const at = Date.now();
	switch (code) {
		case 'bad_url':
			return { code, message: f.bad_url, action: 'edit-url', at };
		case 'blocked_destination':
			return { code, message: f.blocked_destination, action: 'edit-url', at };
		case 'too_many_redirects':
			return { code, message: f.too_many_redirects, action: 'retry', at };
		case 'too_large':
			return { code, message: f.too_large, action: 'download', at };
		case 'timeout':
			return { code, message: f.timeout, action: 'retry', at };
		case 'unsupported_type':
			return { code, message: f.unsupported_type, action: 'edit-url', at };
		case 'upstream_status':
			return { code, message: f.upstream_status, action: 'retry', at };
		case 'rate_limited':
			return {
				code,
				message: f.rate_limited(retryAfter ?? 60),
				action: 'retry',
				retryAfter: retryAfter ?? 60,
				at,
			};
		default:
			return { code: 'unreachable', message: f.unreachable, action: 'retry', at };
	}
}

async function runUrl(): Promise<void> {
	const url = validUrl(urlText.value);
	if (!url) {
		error.value = fetchError('bad_url');
		setPhase('error');
		return;
	}
	const id = begin(url.hostname);
	fetchHost.value = url.hostname;
	setPhase('fetching');
	abort = new AbortController();
	const signal = abort.signal;
	try {
		const res = await fetch('/api/fetch-url', {
			method: 'POST',
			headers: { 'content-type': 'application/json', 'x-provenance-client': '1' },
			body: JSON.stringify({ url: url.href }),
			signal,
		});
		if (!res.ok) {
			let code = 'unreachable';
			try {
				code = ((await res.json()) as { error?: string }).error ?? code;
			} catch {
				/* not JSON: the service could not be reached */
			}
			const retry = Number(res.headers.get('retry-after'));
			error.value = fetchError(code, Number.isFinite(retry) && retry > 0 ? retry : undefined);
			setPhase('error');
			return;
		}
		const bytes = new Uint8Array(await res.arrayBuffer());
		if (signal.aborted || currentJob !== id) return;
		sendAnalyse(id, {
			kind: 'fetched',
			bytes,
			contentType: res.headers.get('x-provenance-content-type') ?? '',
			finalUrl: res.headers.get('x-provenance-final-url') ?? url.href,
		});
	} catch {
		if (signal.aborted) return; // cancel() already set the phase
		error.value = fetchError('unreachable');
		setPhase('error');
	} finally {
		abort = null;
	}
}

export function rerun(key?: string): void {
	const id = begin(storedJob.value?.name ?? 'Stored document');
	startWatchdog();
	setPhase('parsing');
	client.send({ type: 'rerun', id, key });
}

export function cancel(): void {
	const p = phase.value;
	if (p === 'fetching') {
		abort?.abort();
		currentJob = 0;
		setPhase('cancelled');
		return;
	}
	if (p === 'parsing' || p === 'analysing' || p === 'intake-checked') {
		// analysis is synchronous CPU work inside the engine: terminating the worker is the only prompt stop (UX-RES-01)
		stopWatchdog();
		client.restart();
		currentJob = 0;
		setPhase('cancelled');
		client.send({ type: 'discard', id: client.id() });
		refreshRecent();
	}
}

export function newAnalysis(): void {
	result.value = null;
	intakeInfo.value = null;
	error.value = null;
	clearNotices();
	setPhase('empty');
	navigate('#/overview', { replace: true });
	history.replaceState(null, '', location.pathname + location.search);
	client
		.request({ type: 'discard', id: client.id() }, ['recent'])
		.then((r) => {
			recent.value = r.items;
		})
		.catch(() => undefined);
}

/* ---------------- worker messages ---------------- */

function firstPaint(id: number, restored: boolean): void {
	requestAnimationFrame(() =>
		requestAnimationFrame(() => {
			if (currentJob !== id) return;
			wallMs.value = performance.now() - runStartedAt.value;
			// the rate this machine just measured scales the next estimate's prior (rates.ts); a restore is not an analysis
			if (!restored) recordAnalysisRate(result.value?.meta.words ?? 0, wallMs.value);
			setPhase('ready');
			// the overview renders on the next frame; this mark is its first paint (T-PERF-10)
			requestAnimationFrame(() =>
				requestAnimationFrame(() => performance.mark('provenance:painted'))
			);
			if (!location.hash || location.hash === '#') navigate('#/overview', { replace: true });
		})
	);
}

function onMessage(m: FromWorker): void {
	if (m.id !== currentJob) return;
	switch (m.type) {
		case 'progress': {
			touch();
			const p = phase.value;
			if (p === 'parsing' || p === 'analysing' || p === 'intake-checked') {
				progress.value = { stage: m.stage, pct: m.pct, detail: m.detail };
				if (m.stage !== 'read' && p !== 'analysing') setPhase('analysing');
			}
			break;
		}
		case 'intake': {
			touch();
			intakeInfo.value = m.intake;
			sourceName.value = m.intake.name;
			setPhase('intake-checked');
			break;
		}
		case 'result': {
			performance.mark('provenance:result');
			stopWatchdog();
			if (m.restoredAt && m.id === bootRestoreId) markBootRestored(m.payload);
			result.value = m.payload;
			storedJob.value = null;
			setPhase('rendering');
			if (m.restoredAt) {
				restoredNotice = true;
				addNotice(
					T.restoredOn(
						new Date(m.restoredAt).toLocaleString('en-US', {
							dateStyle: 'medium',
							timeStyle: 'short',
						})
					)
				);
			}
			firstPaint(m.id, m.restoredAt !== undefined);
			refreshRecent();
			break;
		}
		case 'stored':
			if (!m.ok) addNotice(T.storageNotice);
			refreshRecent();
			break;
		case 'cancelled':
			stopWatchdog();
			setPhase('cancelled');
			break;
		case 'error': {
			stopWatchdog();
			if (m.code === 'over-limit') {
				error.value = { code: m.code, message: m.message, action: 'choose-file', at: Date.now() };
				setPhase('over-limit');
				break;
			}
			const internal = m.code === 'internal' || m.code === 'model';
			error.value = {
				code: m.code,
				message: internal ? ERRORS.internal(m.message.replace(/\.$/, '')) : m.message,
				action: internal ? 'run-again' : 'choose-file',
				at: Date.now(),
				...(internal ? { detail: ERRORS.diagnosticsId(`${m.id}-${m.code}`) } : {}),
			};
			setPhase('error');
			break;
		}
	}
}

client.subscribe(onMessage);

/* ---------------- recent, restore, boot ---------------- */

export function refreshRecent(): void {
	client
		.request({ type: 'recent', id: client.id() }, ['recent'])
		.then((r) => {
			recent.value = r.items;
		})
		.catch(() => undefined);
}

export function openRecent(item: RecentItem): void {
	if (item.stale) {
		rerun(item.key);
		return;
	}
	restore(item.key, item.name);
}

function restore(key: string, name: string): number {
	const id = begin(name);
	setPhase('restoring');
	client.send({ type: 'restore', id, key });
	return id;
}

export function removeRecent(item: RecentItem): void {
	client
		.request({ type: 'remove', id: client.id(), key: item.key }, ['recent'])
		.then((r) => {
			recent.value = r.items;
		})
		.catch(() => undefined);
}

export function discardInterrupted(): void {
	storedJob.value = null;
	setPhase('empty');
	client
		.request({ type: 'discard', id: client.id() }, ['recent'])
		.then((r) => {
			recent.value = r.items;
		})
		.catch(() => undefined);
}

/** On load: a stored job with a result is restored; one without is offered "Run again"; else the intake (§4.6). */
export async function boot(): Promise<void> {
	try {
		const probed = await client.request({ type: 'probe', id: client.id() }, ['probed']);
		refreshRecent();
		const job = probed.job;
		if (!job) return;
		storedJob.value = job;
		sourceName.value = job.name;
		if (job.resultKey) bootRestoreId = restore(job.resultKey, job.name);
		else setPhase('interrupted');
	} catch {
		/* storage or worker unavailable: start empty */
	}
}

/** Used by the word-count readout and tests. */
export const formatWords = (n: number): string => fmtInt(n);
export function wasRestored(): boolean {
	return restoredNotice;
}

/* ---------------- exports ---------------- */

export async function exportAs(format: ExportFormat): Promise<void> {
	const vm = result.value;
	if (!vm) return;
	const id = client.id();
	const send = (withPayload: boolean) =>
		client.request(
			{
				type: 'export',
				id,
				format,
				docHash: vm.meta.docHash,
				...(withPayload ? { payload: vm } : {}),
			},
			['exported']
		);
	let reply: Awaited<ReturnType<typeof send>>;
	try {
		reply = await send(false);
	} catch (e) {
		if ((e as Error).message !== 'payload-needed') throw e;
		// the worker holds no copy (storage unavailable): send the result back once
		reply = await client.request(
			{ type: 'export', id: client.id(), format, docHash: vm.meta.docHash, payload: vm },
			['exported']
		);
	}
	const url = URL.createObjectURL(reply.blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = reply.filename;
	document.body.append(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
